import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import platformConfigService from '../../../services/platformConfigService';

const FeeStructureManagement = ({ data, onUpdate }) => {
  const [fees, setFees] = useState({
    platformCommission: '15',
    paymentProcessingFee: '2.9',
    listingFee: '0',
    promotionalDiscount: '0',
    ownerPayoutPercentage: '85',
    stripeFixedFee: '0.30'
  });
  const [revenueProjection, setRevenueProjection] = useState({
    sampleBookingAmount: 500,
    platformRevenue: 0,
    ownerPayout: 0,
    processingCost: 0
  });
  const [saving, setSaving] = useState(false);

  // Load data from props
  useEffect(() => {
    if (data) {
      setFees({
        platformCommission: String(data?.platformCommission || '15'),
        paymentProcessingFee: String(data?.paymentProcessingFee || '2.9'),
        listingFee: String(data?.listingFee || '0'),
        promotionalDiscount: String(data?.promotionalDiscount || '0'),
        ownerPayoutPercentage: String(data?.ownerPayoutPercentage || '85'),
        stripeFixedFee: String(data?.stripeFixedFee || '0.30')
      });
    }
  }, [data]);

  useEffect(() => {
    calculateRevenueImpact();
  }, [fees, revenueProjection?.sampleBookingAmount]);

  const handleInputChange = (field, value) => {
    setFees({ ...fees, [field]: value });
  };

  const calculateRevenueImpact = () => {
    const booking = parseFloat(revenueProjection?.sampleBookingAmount) || 0;
    const commission = (booking * parseFloat(fees?.platformCommission || 0)) / 100;
    const processing = (booking * parseFloat(fees?.paymentProcessingFee || 0)) / 100 + parseFloat(fees?.stripeFixedFee || 0);
    const owner = booking - commission - processing;

    setRevenueProjection({
      ...revenueProjection,
      platformRevenue: commission?.toFixed(2),
      ownerPayout: owner?.toFixed(2),
      processingCost: processing?.toFixed(2)
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await platformConfigService?.updateFeeStructure(fees);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to save fee structure:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={20} className="text-primary" aria-hidden="true" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground">Fee Structure Management</h2>
              <p className="font-body text-sm text-muted-foreground">Configure platform commission rates and payment processing fees</p>
            </div>
          </div>
          <Button
            size="sm"
            iconName="Save"
            onClick={handleSave}
            loading={saving}
            aria-label="Save fee structure"
          >
            Save Fees
          </Button>
        </div>
      </div>

      {/* Commission Rates */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-md mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Percent" size={18} className="text-primary" strokeWidth={2} aria-hidden="true" />
            <span>Platform Commission Rates</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Platform Commission (%)"
              type="number"
              value={fees?.platformCommission}
              onChange={(e) => handleInputChange('platformCommission', e?.target?.value)}
              description="Percentage taken from each booking"
              required
              aria-required="true"
            />
            <Input
              label="Owner Payout (%)"
              type="number"
              value={fees?.ownerPayoutPercentage}
              onChange={(e) => handleInputChange('ownerPayoutPercentage', e?.target?.value)}
              description="Percentage paid to trailer owners"
              required
              disabled
              aria-disabled="true"
            />
            <Input
              label="Promotional Discount (%)"
              type="number"
              value={fees?.promotionalDiscount}
              onChange={(e) => handleInputChange('promotionalDiscount', e?.target?.value)}
              description="Temporary discount for promotions"
              aria-describedby="promo-discount-help"
            />
          </div>
        </div>
      </div>

      {/* Payment Processing Fees */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-md mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="CreditCard" size={18} className="text-secondary" strokeWidth={2} aria-hidden="true" />
            <span>Payment Processing Fees</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Processing Fee (%)"
              type="number"
              value={fees?.paymentProcessingFee}
              onChange={(e) => handleInputChange('paymentProcessingFee', e?.target?.value)}
              description="Stripe/payment gateway percentage"
              required
              aria-required="true"
            />
            <Input
              label="Fixed Transaction Fee ($)"
              type="number"
              value={fees?.stripeFixedFee}
              onChange={(e) => handleInputChange('stripeFixedFee', e?.target?.value)}
              description="Per-transaction fixed cost"
              required
              aria-required="true"
            />
            <Input
              label="Listing Fee ($)"
              type="number"
              value={fees?.listingFee}
              onChange={(e) => handleInputChange('listingFee', e?.target?.value)}
              description="One-time fee to list a trailer"
              aria-describedby="listing-fee-help"
            />
          </div>
        </div>
      </div>

      {/* Revenue Impact Calculator */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-success/5 border border-success/20 rounded-md">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Calculator" size={18} className="text-success" strokeWidth={2} aria-hidden="true" />
            <span>Revenue Impact Projections</span>
          </h3>
          <div className="space-y-4">
            <Input
              label="Sample Booking Amount ($)"
              type="number"
              value={revenueProjection?.sampleBookingAmount}
              onChange={(e) => setRevenueProjection({ ...revenueProjection, sampleBookingAmount: e?.target?.value })}
              description="Enter amount to calculate fee breakdown"
              aria-describedby="sample-booking-help"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" role="region" aria-label="Revenue breakdown">
              <article className="p-4 bg-primary/10 rounded-md" aria-labelledby="platform-revenue-label">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="TrendingUp" size={16} className="text-primary" aria-hidden="true" />
                  <span id="platform-revenue-label" className="font-body text-sm text-muted-foreground">Platform Revenue</span>
                </div>
                <p className="font-heading text-2xl font-bold text-foreground">${revenueProjection?.platformRevenue}</p>
                <p className="font-caption text-xs text-muted-foreground mt-1">{fees?.platformCommission}% commission</p>
              </article>
              <article className="p-4 bg-success/10 rounded-md" aria-labelledby="owner-payout-label">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Wallet" size={16} className="text-success" aria-hidden="true" />
                  <span id="owner-payout-label" className="font-body text-sm text-muted-foreground">Owner Payout</span>
                </div>
                <p className="font-heading text-2xl font-bold text-foreground">${revenueProjection?.ownerPayout}</p>
                <p className="font-caption text-xs text-muted-foreground mt-1">After fees deducted</p>
              </article>
              <article className="p-4 bg-warning/10 rounded-md" aria-labelledby="processing-cost-label">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="CreditCard" size={16} className="text-warning" aria-hidden="true" />
                  <span id="processing-cost-label" className="font-body text-sm text-muted-foreground">Processing Cost</span>
                </div>
                <p className="font-heading text-2xl font-bold text-foreground">${revenueProjection?.processingCost}</p>
                <p className="font-caption text-xs text-muted-foreground mt-1">{fees?.paymentProcessingFee}% + ${fees?.stripeFixedFee}</p>
              </article>
            </div>
          </div>
        </div>
      </div>

      {/* Fee Comparison Table */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <h3 className="font-heading text-base font-semibold text-foreground mb-4">Industry Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full" role="table" aria-label="Fee comparison table">
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="text-left font-body text-sm font-semibold text-foreground py-3 px-4">Platform</th>
                <th scope="col" className="text-left font-body text-sm font-semibold text-foreground py-3 px-4">Commission</th>
                <th scope="col" className="text-left font-body text-sm font-semibold text-foreground py-3 px-4">Processing Fee</th>
                <th scope="col" className="text-left font-body text-sm font-semibold text-foreground py-3 px-4">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border">
                <td className="font-body text-sm text-foreground py-3 px-4 font-semibold">TrailerHub (Current)</td>
                <td className="font-body text-sm text-foreground py-3 px-4">{fees?.platformCommission}%</td>
                <td className="font-body text-sm text-foreground py-3 px-4">{fees?.paymentProcessingFee}% + ${fees?.stripeFixedFee}</td>
                <td className="py-3 px-4">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                    Active
                  </span>
                </td>
              </tr>
              <tr className="border-b border-border">
                <td className="font-body text-sm text-muted-foreground py-3 px-4">Industry Average</td>
                <td className="font-body text-sm text-muted-foreground py-3 px-4">12-18%</td>
                <td className="font-body text-sm text-muted-foreground py-3 px-4">2.9% + $0.30</td>
                <td className="py-3 px-4">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                    Reference
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Info" size={16} aria-hidden="true" />
            <span>Fee changes apply to new bookings only. Existing bookings retain original rates.</span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            iconName="Download"
            aria-label="Export fee report"
          >
            Export Fee Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeeStructureManagement;